
import { useState } from 'react';

const CTASection = () => {
  const [language, setLanguage] = useState('fr');

  const content = {
    fr: {
      title: "Prêt à rejoindre Wald Bladi ?",
      subtitle: "L'entraide communautaire maghrébine à Rouen",
      description: "Inscrivez-vous gratuitement et commencez à aider ou être aidé dès aujourd'hui. Notre communauté vous attend !",
      features: [
        "Inscription gratuite et rapide",
        "Services disponibles 7j/7",
        "Paiement sécurisé entre membres",
        "Support communautaire bienveillant"
      ],
      buttons: {
        primary: "Rejoindre la communauté",
        secondary: "En savoir plus"
      },
      contact: {
        title: "Une question ?",
        phone: "06 12 34 56 78",
        email: "contact@waldbladi.fr"
      }
    },
    ar: {
      title: "مستعد للانضمام إلى ولد بلادي؟",
      subtitle: "التعاون المجتمعي المغاربي في روان",
      description: "سجل مجاناً وابدأ في المساعدة أو الحصول على المساعدة من اليوم. مجتمعنا في انتظارك!",
      features: [
        "تسجيل مجاني وسريع",
        "خدمات متاحة 7 أيام في الأسبوع",
        "دفع آمن بين الأعضاء",
        "دعم مجتمعي ودود"
      ],
      buttons: {
        primary: "انضم إلى المجتمع",
        secondary: "اعرف المزيد"
      },
      contact: {
        title: "لديك سؤال؟",
        phone: "06 12 34 56 78",
        email: "contact@waldbladi.fr"
      }
    }
  };

  const currentContent = content[language as keyof typeof content];

  return (
    <section 
      className="relative py-20 bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://public.readdy.ai/ai/img_res/0f06e9f2ba3dc624a77a0880da5574c9.jpg')`
      }}
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Language Toggle */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg p-1">
            <button
              onClick={() => setLanguage('fr')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                language === 'fr' 
                  ? 'bg-white text-orange-600 shadow-lg' 
                  : 'text-white hover:bg-white/20'
              }`}
            >
              Français
            </button>
            <button
              onClick={() => setLanguage('ar')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                language === 'ar' 
                  ? 'bg-white text-orange-600 shadow-lg' 
                  : 'text-white hover:bg-white/20'
              }`}
            >
              العربية
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`${language === 'ar' ? 'text-right' : 'text-left'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {currentContent.title}
            </h2>
            <p className="text-xl text-orange-300 font-semibold mb-6">
              {currentContent.subtitle}
            </p>
            <p className="text-lg text-gray-200 mb-8 leading-relaxed">
              {currentContent.description}
            </p>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {currentContent.features.map((feature, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center bg-orange-500 rounded-full mr-3">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-white">{feature}</span>
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <button className="bg-orange-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap shadow-lg">
                <i className="ri-user-add-line mr-2"></i>
                {currentContent.buttons.primary}
              </button>
              <button className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap border border-white/30">
                <i className="ri-information-line mr-2"></i>
                {currentContent.buttons.secondary}
              </button>
            </div>
          </div>

          {/* Right Content - Contact */}
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              {currentContent.contact.title}
            </h3>
            
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-full mr-4">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-orange-300 font-medium">Téléphone / هاتف</p>
                  <p className="text-white text-lg font-semibold">{currentContent.contact.phone}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-full mr-4">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-orange-300 font-medium">Email / بريد إلكتروني</p>
                  <p className="text-white text-lg font-semibold">{currentContent.contact.email}</p>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button className="bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap w-full">
                <i className="ri-message-3-line mr-2"></i>
                {language === 'fr' ? 'Nous contacter' : 'اتصل بنا'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
